// context/EnrollmentContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  collection, 
  getDocs, 
  query, 
  where, 
  onSnapshot 
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { useAuth } from './AuthContext';

// Create context
const EnrollmentContext = createContext();

// Custom hook to use the enrollment context
export const useEnrollment = () => {
  return useContext(EnrollmentContext);
};

// Provider component
export const EnrollmentProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [enrollments, setEnrollments] = useState([]); // All enrollments
  const [activeEnrollments, setActiveEnrollments] = useState([]); // Only active enrollments
  const [isLocked, setIsLocked] = useState(true); // Overall lock status
  const [loading, setLoading] = useState(true);

  // Set up real-time listener for enrollments
  useEffect(() => {
    let unsubscribe;

    if (currentUser?.uid) {
      const enrollmentsQuery = query(
        collection(db, "enrollments"),
        where("userId", "==", currentUser.uid),
        where("status", "==", "completed")
      );

      unsubscribe = onSnapshot(
        enrollmentsQuery,
        (snapshot) => {
          const allEnrollments = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            // Convert Firestore timestamps to Date objects
            enrollmentDate: doc.data().enrollmentDate?.toDate ? 
              doc.data().enrollmentDate.toDate() : 
              new Date(doc.data().enrollmentDate),
            expiryDate: doc.data().expiryDate?.toDate ? 
              doc.data().expiryDate.toDate() : 
              new Date(doc.data().expiryDate),
          }));

          // Filter active enrollments (not expired)
          const currentDate = new Date();
          const activeOnly = allEnrollments.filter((enrollment) => {
            return enrollment.expiryDate && enrollment.expiryDate > currentDate;
          });

          // Create simplified active enrollments list
          const simplifiedActive = activeOnly.map(enrollment => ({
            courseId: enrollment.courseId,
            courseName: enrollment.courseTitle,
            isActive: true,
            language: enrollment.language || 'N/A',
            expiryDate: enrollment.expiryDate,
            enrollmentDate: enrollment.enrollmentDate
          }));

          setEnrollments(allEnrollments);
          setActiveEnrollments(simplifiedActive);
          setIsLocked(simplifiedActive.length === 0); // Locked if no active enrollments
          setLoading(false);
        },
        (error) => {
          console.error("Enrollment listener error:", error);
          setLoading(false);
        }
      );
    } else {
      // No user logged in
      setEnrollments([]);
      setActiveEnrollments([]);
      setIsLocked(true);
      setLoading(false);
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser]);

  // Check if user has active subscription for a specific course
  const hasActiveSubscription = (courseId) => {
    return activeEnrollments.some(enrollment => enrollment.courseId === courseId);
  };

  // Check if specific course is locked
  const isCourseLockedCheck = (courseId) => {
    return !hasActiveSubscription(courseId);
  };

  // Get active subscription for a course
  const getActiveSubscription = (courseId) => {
    return activeEnrollments.find(enrollment => enrollment.courseId === courseId) || null;
  };

  // Get course language for a specific course
  const getCourseLanguage = (courseId) => {
    const subscription = getActiveSubscription(courseId);
    return subscription ? subscription.language : null;
  };

  // Context value
  const value = {
    // Main data
    enrollments, // All enrollments (raw data)
    activeEnrollments, // Simplified active enrollments list
    isLocked, // Overall lock status (true if no active subscriptions)
    loading,
    
    // Helper functions
    hasActiveSubscription, // Check if specific course is subscribed
    isCourseLockedCheck, // Check if specific course is locked
    getActiveSubscription, // Get active subscription details for a course
    getCourseLanguage, // Get language for a specific course
  };

  return (
    <EnrollmentContext.Provider value={value}>
      {children}
    </EnrollmentContext.Provider>
  );
};

export default EnrollmentProvider;